/**
 * Per-attempt state of the MCP authorization landing page, shared by the synchronous bootstrap
 * capture and the Angular page. A plain module on purpose: it runs before Angular and Sentry
 * exist, so it has no framework or workspace imports.
 *
 * The interaction secret lives only here: in this tab's sessionStorage (it survives a reload and
 * a sign-in round trip) or, when storage cannot be used, in ONE module-level memory map shared by
 * every caller of this module. Never localStorage or BroadcastChannel.
 */

export type McpAttempt = {
  requestId: string
  secret: string
  /** Epoch milliseconds. */
  createdAt: number
}

export const MCP_ATTEMPT_KEY_PREFIX = 'ti.mcp.attempt.'
export const MCP_ATTEMPT_TTL_MS = 10 * 60 * 1000
export const MCP_ATTEMPT_MAX_COUNT = 5

// Mirror MCP_AUTH_REQUEST_ID_PATTERN and MCP_AUTH_INTERACTION_SECRET_PATTERN of @ti/contracts,
// which this bootstrap-time module cannot import; the spec pins them to the contract.
const REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{32}$/
const INTERACTION_SECRET_PATTERN = /^[A-Za-z0-9_-]{43}$/

export const isMcpRequestId = (value: unknown): value is string =>
  typeof value === 'string' && REQUEST_ID_PATTERN.test(value)

export const isMcpInteractionSecret = (value: unknown): value is string =>
  typeof value === 'string' && INTERACTION_SECRET_PATTERN.test(value)

export type McpAttemptStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key'> & {
  readonly length: number
}

export type McpAttemptStore = {
  /** Null for a malformed id, an unknown, expired or corrupted attempt. */
  get(requestId: string): McpAttempt | null
  /** False when the request id or the secret has the wrong shape; nothing is stored then. */
  put(attempt: Pick<McpAttempt, 'requestId' | 'secret'>): boolean
  remove(requestId: string): void
  /** Sign-out: drops every attempt from sessionStorage and from the shared memory fallback. */
  clearAll(): void
}

export type McpAttemptStoreDeps = {
  /** Resolved on every access, because merely reading `window.sessionStorage` can throw. */
  storage?: () => McpAttemptStorage | null
  memory?: Map<string, McpAttempt>
  now?: () => number
}

const sharedMemory = new Map<string, McpAttempt>()

const nativeStorage = (): McpAttemptStorage | null =>
  typeof window === 'undefined' ? null : window.sessionStorage

const storageKey = (requestId: string) => `${MCP_ATTEMPT_KEY_PREFIX}${requestId}`

const parseAttempt = (raw: string, requestId: string): McpAttempt | null => {
  try {
    const value: unknown = JSON.parse(raw)
    if (typeof value !== 'object' || value === null) {
      return null
    }
    const { requestId: storedId, secret, createdAt } = value as Record<string, unknown>
    if (
      storedId !== requestId ||
      !isMcpInteractionSecret(secret) ||
      typeof createdAt !== 'number' ||
      !Number.isFinite(createdAt)
    ) {
      return null
    }
    return { requestId: storedId, secret, createdAt }
  } catch {
    return null
  }
}

export function createMcpAttemptStore(deps: McpAttemptStoreDeps = {}): McpAttemptStore {
  const resolveStorage = deps.storage ?? nativeStorage
  const memory = deps.memory ?? sharedMemory
  const now = deps.now ?? (() => Date.now())

  /** Every storage access can throw (disabled, blocked, quota): a failure is just "unavailable". */
  const withStorage = <T>(action: (storage: McpAttemptStorage) => T): T | undefined => {
    try {
      const storage = resolveStorage()
      return storage ? action(storage) : undefined
    } catch {
      return undefined
    }
  }

  // A future createdAt (clock change or tampering) counts as expired so it cannot outlive the TTL.
  const isLive = (attempt: McpAttempt) => {
    const age = now() - attempt.createdAt
    return age >= 0 && age < MCP_ATTEMPT_TTL_MS
  }

  const removeFromStorage = (requestId: string) =>
    withStorage((storage) => storage.removeItem(storageKey(requestId)))

  const remove = (requestId: string) => {
    removeFromStorage(requestId)
    memory.delete(requestId)
  }

  const readFromStorage = (requestId: string): McpAttempt | null => {
    const raw = withStorage((storage) => storage.getItem(storageKey(requestId)))
    if (typeof raw !== 'string') {
      return null
    }
    const attempt = parseAttempt(raw, requestId)
    if (!attempt || !isLive(attempt)) {
      removeFromStorage(requestId)
      return null
    }
    return attempt
  }

  const readFromMemory = (requestId: string): McpAttempt | null => {
    const attempt = memory.get(requestId)
    if (!attempt) {
      return null
    }
    if (!isLive(attempt)) {
      memory.delete(requestId)
      return null
    }
    return attempt
  }

  const get = (requestId: string): McpAttempt | null => {
    if (!isMcpRequestId(requestId)) {
      return null
    }
    const attempt = readFromStorage(requestId) ?? readFromMemory(requestId)
    return attempt ? { ...attempt } : null
  }

  const storedRequestIds = (): string[] =>
    withStorage((storage) => {
      const ids: string[] = []
      for (let index = 0; index < storage.length; index++) {
        const key = storage.key(index)
        if (key?.startsWith(MCP_ATTEMPT_KEY_PREFIX)) {
          ids.push(key.slice(MCP_ATTEMPT_KEY_PREFIX.length))
        }
      }
      return ids
    }) ?? []

  /** Live attempts from both backends; expired and corrupted ones are dropped on the way. */
  const liveAttempts = (): McpAttempt[] => {
    const attempts = new Map<string, McpAttempt>()
    for (const requestId of new Set([...storedRequestIds(), ...memory.keys()])) {
      const attempt = isMcpRequestId(requestId)
        ? (readFromStorage(requestId) ?? readFromMemory(requestId))
        : null
      if (attempt) {
        attempts.set(requestId, attempt)
      } else {
        remove(requestId)
      }
    }
    return [...attempts.values()]
  }

  const put = ({ requestId, secret }: Pick<McpAttempt, 'requestId' | 'secret'>): boolean => {
    if (!isMcpRequestId(requestId) || !isMcpInteractionSecret(secret)) {
      return false
    }
    const attempt: McpAttempt = { requestId, secret, createdAt: now() }

    const others = liveAttempts()
      .filter((existing) => existing.requestId !== requestId)
      .sort((a, b) => a.createdAt - b.createdAt)
    for (const evicted of others.slice(
      0,
      Math.max(0, others.length - (MCP_ATTEMPT_MAX_COUNT - 1)),
    )) {
      remove(evicted.requestId)
    }

    const persisted = withStorage((storage) => {
      storage.setItem(storageKey(requestId), JSON.stringify(attempt))
      return true
    })
    if (persisted) {
      memory.delete(requestId)
    } else {
      memory.set(requestId, attempt)
      removeFromStorage(requestId)
    }
    return true
  }

  const clearAll = () => {
    for (const requestId of storedRequestIds()) {
      removeFromStorage(requestId)
    }
    memory.clear()
  }

  return { get, put, remove, clearAll }
}

export const mcpAttemptStore: McpAttemptStore = createMcpAttemptStore()
